import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const invalidatePortal = () => window.dispatchEvent(new Event('portal-data-changed'));

export default function usePortalQuery(key, loader, enabled = true) {
  const { user, sessionId } = useAuth();
  const identityKey = JSON.stringify([sessionId, user?.username, user?.role, user?.investor_id, key]);
  const [state, setState] = useState({ key: '', loading: true });
  const [revision, setRevision] = useState(0);
  const loadRef = useRef(loader);
  loadRef.current = loader;
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const focus = () => { if (document.visibilityState !== 'hidden') refresh(); };
    window.addEventListener('focus', focus);
    window.addEventListener('portal-data-changed', refresh);
    return () => {
      window.removeEventListener('focus', focus);
      window.removeEventListener('portal-data-changed', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setState({ key: identityKey, loading: true });
    Promise.resolve().then(() => loadRef.current(controller.signal)).then((data) => {
      if (!controller.signal.aborted) setState({ key: identityKey, data, loading: false, updatedAt: new Date() });
    }).catch((error) => {
      if (!controller.signal.aborted && error.name !== 'AbortError') setState({ key: identityKey, error, loading: false });
    });
    const expirationRefresh = setTimeout(refresh, 50 * 60 * 1000);
    return () => { controller.abort(); clearTimeout(expirationRefresh); };
  }, [identityKey, revision, enabled, refresh]);

  if (!enabled) return { loading: false, refresh };
  return { ...(state.key === identityKey ? state : { loading: true }), refresh };
}
